<template>
    <div v-if="filter.selection" :id="`filter-${filter.id}`" class="slider is-open">
        <h3 class="slider__title">{{ filter.title }}</h3>

        <div class="slider__body">
            <vue-slider
                ref="vueSlider"
                v-model="sliderValue"
                :min="Math.floor(actualUnit.minValue)"
                :max="Math.ceil(actualUnit.maxValue)"
                :interval="getInterval"
                :dot-attrs="{ 'aria-label': 'Select ' + filter.title }"
                @drag-end="dragEnd"
            ></vue-slider>
            <div class="slider__form">
                <div class="slider__inputs" :class="{ 'slider__inputs--xl': !Array.isArray(value) }">
                    <input
                        v-if="!Array.isArray(value)"
                        v-model="value"
                        type="number"
                        :min="actualUnit.minValue"
                        :max="actualUnit.maxValue"
                        class="slider__input"
                        :aria-label="'Select ' + filter.title"
                        @change="change"
                        @keyup="change"
                    />
                    <input
                        v-else
                        v-model="value[0]"
                        type="number"
                        :min="actualUnit.minValue"
                        :max="actualUnit.maxValue"
                        class="slider__input"
                        :aria-label="'Select ' + filter.title"
                        @change="change"
                        @keyup="change"
                    />
                    <select
                        v-if="filter.units && filter.units.length > 1"
                        v-model="actualUnitId"
                        class="slider__input slider__input--select"
                        @change="unitChanged"
                    >
                        <option
                            v-for="(unit, key) in filter.units"
                            :key="key"
                            :selected="unit.id === filter.selection.unit"
                            :value="unit.id"
                        >
                            {{ unit.title }}
                        </option>
                    </select>
                    <span v-else class="slider__input slider__input--addon">{{ actualUnit.title }}</span>
                </div>

                <div v-if="Array.isArray(value)" class="slider__separator">-</div>
                <div v-if="Array.isArray(value)" class="slider__inputs">
                    <input
                        v-model="value[1]"
                        type="number"
                        :min="actualUnit.minValue"
                        :max="actualUnit.maxValue"
                        class="slider__input"
                        :aria-label="'Select ' + filter.title"
                        @change="change"
                        @keyup="change"
                    />
                    <select
                        v-if="filter.units && filter.units.length > 1"
                        v-model="actualUnitId"
                        class="slider__input slider__input--select"
                        @change="unitChanged"
                    >
                        <option
                            v-for="(unit, index) in filter.units"
                            :key="index"
                            :selected="unit.id === filter.selection.unit"
                            :value="unit.id"
                        >
                            {{ unit.title }}
                        </option>
                    </select>

                    <span v-else class="slider__input slider__input--addon">{{ actualUnit.title }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { PropType } from 'vue'
import VueSlider from 'vue-3-slider-component'
import Filter from '~/models/Filters/Filter'
import Unit from '~/models/Filters/Unit'
import 'vue-slider-component/theme/default.css'
import 'vue-slider-component/dist-css/vue-slider-component.css'

export default defineNuxtComponent({
    components: {
        VueSlider,
    },
    props: {
        filter: {
            type: Object as PropType<Filter>,
            required: true,
        },
    },

    watch: {
        filter(newFilter) {
            this.value = newFilter.selection.from
            this.sliderValue = newFilter.selection.from
            if (newFilter.type === 'range') {
                this.value = [newFilter.selection.from, newFilter.selection.to]
                this.sliderValue = [newFilter.selection.from, newFilter.selection.to]
            }

            // init actualUnit
            this.actualUnit = this.findUnitById(this.filter.selection.unit) || this.filter.units[0] || null
            this.actualUnitId = this.filter.selection.unit
        },

        sliderValue(newSliderValue) {
            if (Array.isArray(this.value)) {
                this.value[0] = newSliderValue[0]
                this.value[1] = newSliderValue[1]
            } else {
                this.value = newSliderValue
            }
        },
    },

    data() {
        if (!this.filter.selection || !this.filter.units) {
            return {}
        }

        // init value
        let value: number | number[] = this.filter.selection.from
        let sliderValue: number | number[] = this.filter.selection.from
        if (this.filter.type === 'range') {
            value = [this.filter.selection.from, this.filter.selection.to]
            sliderValue = [this.filter.selection.from, this.filter.selection.to]
        }

        // init actualUnit
        const actualUnit = this.findUnitById(this.filter.selection.unit) || this.filter.units[0] || null
        const actualUnitId = this.filter.selection.unit
        const timeoutId: NodeJS.Timeout | null = null

        return {
            value,
            sliderValue,
            actualUnit,
            actualUnitId,
            timeoutId,
        }
    },

    computed: {
        getInterval(): Number {
            return 1
        },

        urlMask(): string {
            return this.filter.urlMask
        },
    },

    methods: {
        findUnitById(unitId: number): Unit | null {
            for (const unit of this.filter.units) {
                if (unit.id === unitId) {
                    return unit
                }
            }

            return null
        },

        validateValue(): void {
            if (this.actualUnit === null) {
                return
            }

            if (Array.isArray(this.value)) {
                if (this.value[0] < this.actualUnit.minValue) {
                    this.value[0] = this.sliderValue[0] = this.actualUnit.minValue
                }
                if (this.value[0] > this.actualUnit.maxValue) {
                    this.value[0] = this.sliderValue[0] = this.actualUnit.maxValue
                }

                if (this.value[1] < this.actualUnit.minValue) {
                    this.value[1] = this.sliderValue[1] = this.actualUnit.minValue
                }
                if (this.value[1] > this.actualUnit.maxValue) {
                    this.value[1] = this.sliderValue[1] = this.actualUnit.maxValue
                }
            } else {
                if (this.value < this.actualUnit.minValue) {
                    this.value = this.sliderValue = this.actualUnit.minValue
                }
                if (this.value > this.actualUnit.maxValue) {
                    this.value = this.sliderValue = this.actualUnit.maxValue
                }
            }
        },

        unitChanged(event: Event): void {
            this.actualUnitId = parseInt((event.currentTarget as HTMLSelectElement).value)
            this.actualUnit = this.findUnitById(this.actualUnitId) || this.filter.units[0] || null

            this.$nextTick(function () {
                if (Array.isArray(this.value)) {
                    this.value = [this.actualUnit.minValue, this.actualUnit.maxValue]
                    this.sliderValue = [this.actualUnit.minValue, this.actualUnit.maxValue]
                } else {
                    this.value = this.sliderValue = this.actualUnit.minValue
                }
            })
        },

        dragEnd(): void {
            this.change()
        },

        change(): void {
            clearTimeout(this.timeoutId)
            this.timeoutId = setTimeout(async () => {
                this.validateValue()
                let url = ''
                if (Array.isArray(this.value)) {
                    url = this.urlMask
                        .replace('%from%', Math.min(this.value[0], this.value[1]).toString())
                        .replace('%to%', Math.max(this.value[0], this.value[1]).toString())
                        .replace('%unit%', this.actualUnitId || '')
                } else {
                    url = this.urlMask
                        .replace('%from%', this.value.toString())
                        .replace('%to%', '')
                        .replace('%unit%', this.actualUnitId || '')
                }

                url = `${url}#filter-${this.filter.id}`

                await safeNavigateTo(url)
            }, 1500)
        },
    },
})
</script>

<style lang="scss">
@import 'assets/scss/tools/mixins';

.slider {
    &__form {
        display: flex;
        padding: 1.5rem 0 0;
    }

    &__inputs {
        position: relative;
        display: flex;
        flex-wrap: wrap;
        align-items: stretch;
        margin: 0 auto;
        width: 100%;

        &--xl {
            width: 70%;

            .slider__input--select {
                max-width: 150px;
            }
        }
    }

    &__input {
        background-color: $color-grey;
        border: none;
        text-align: end;
        position: relative;
        flex: 1 1 auto;
        width: 1%;
        min-width: 0;
        border-radius: 10px 0 0 10px;
        padding: 0.5rem 0;
        line-height: 1.5em;
        font-size: 16px;

        &--addon {
            align-items: center;
            border-radius: 0 10px 10px 0;
            text-align: center;
            padding-left: 0;
            max-width: 50px;
        }

        &--select {
            align-items: center;
            border-radius: 0 10px 10px 0;
            text-align: center;
            padding-left: 0;
            max-width: 50px;
        }
    }

    &__separator {
        align-self: center;
        padding: 0 10px;
    }

    &__title {
        font-size: 1rem;
        cursor: pointer;
        display: flex;
        justify-content: space-between;
        margin: 0 0 1rem 0;
        padding: 1rem 0 0 0;

        &::after {
            margin-right: 2px;
            transition: all 0.3s ease-in;

            @include ico-arrow;
        }
    }

    .slider__body {
        padding: 0;
        max-height: 0;
        overflow: hidden;
        transition: all 0.3s ease-in;
    }

    &.is-open {
        .slider__body {
            padding: 10px 5px 15px 5px;
            max-height: 300px;
            transition: max-height 0.3s ease-in;
            overflow: visible;
        }

        .slider__title {
            &::after {
                top: 5px;
                position: relative;
                transform: rotate(315deg);
            }
        }
    }
}

.vue-slider {
    margin: 0 10px;
}

.vue-slider-process {
    background-color: $color-purple !important;
}

.vue-slider-dot-handle {
    box-shadow: none !important;
    background-color: $color-purple !important;
    width: 25px !important;
    height: 25px !important;
    margin-top: -6px;
    margin-left: -6px;
}

.vue-range-slider.slider-component {
    padding: 0 2px;

    .slider {
        background-color: $color-grey-dark;

        .slider-dot {
            box-shadow: none;
            background-color: $color-cta;
            cursor: ew-resize;
        }

        .slider-process {
            background-color: $color-cta;
        }
    }
}

@media (max-width: 768px) {
    .vue-slider-dot-tooltip {
        display: none;
    }
}
</style>
